/**
 * STORY-PAYOUT-1 — Payouts page (agents + affiliate owners).
 *
 * Shows commission balance and the Stripe Connect Express onboarding state.
 * - Not onboarded → "Set up payouts" starts the Stripe-hosted onboarding.
 * - Onboarded but payouts not enabled → "Finish verification" re-opens it.
 * - Onboarded + enabled → balance + "View payout dashboard" (Stripe Express).
 *
 * Bank details are entered with Stripe directly; we never see or store them.
 * HQ pays commission via Connect transfer (admin Commissions panel).
 */

import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useApi } from '@/hooks/useApi';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, BadgeCheck, CreditCard, ExternalLink, Loader2, Wallet } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

interface ConnectStatus {
  payee_kind: 'agent' | 'affiliate';
  connect_account_id: string | null;
  onboarded: boolean;
  payouts_enabled: boolean;
  country: string | null;
  needs_country: boolean;
  pending_cents: number;
  paid_cents: number;
}

function formatGBP(cents: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(cents / 100);
}

/**
 * Countries we pay out to. UK is domestic; the rest are paid cross-border via
 * Stripe Global Payouts (recipient receives their local currency). Stripe
 * validates the final list, so an unsupported pick surfaces as an API error.
 */
const PAYOUT_COUNTRIES: { code: string; name: string }[] = [
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'IE', name: 'Ireland' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'AT', name: 'Austria' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'PL', name: 'Poland' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'RO', name: 'Romania' },
  { code: 'HU', name: 'Hungary' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'HR', name: 'Croatia' },
  { code: 'SG', name: 'Singapore' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'JP', name: 'Japan' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'TH', name: 'Thailand' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'MX', name: 'Mexico' },
  { code: 'BR', name: 'Brazil' },
  { code: 'IN', name: 'India' },
];

export default function PayoutsPage() {
  const { api } = useApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const [actionLoading, setActionLoading] = useState(false);
  const [country, setCountry] = useState('GB');

  const {
    data: status,
    isLoading,
    error,
    refetch,
  } = useQuery<ConnectStatus>({
    queryKey: ['connect-status'],
    queryFn: async () => {
      const res = await api.get<ConnectStatus>('/connect/status');
      if (!res.success || !res.data) throw new Error(res.error || 'Failed to load payout status');
      return res.data;
    },
  });

  // Pre-select the country selector with any country we already hold.
  useEffect(() => {
    if (status?.country) setCountry(status.country);
  }, [status?.country]);

  // When Stripe redirects back (?onboarded=1), re-pull the live account flags
  // then clean the URL so a refresh doesn't re-trigger.
  useEffect(() => {
    if (searchParams.get('onboarded') === '1' || searchParams.get('refresh') === '1') {
      api.post('/connect/refresh').then(() => {
        refetch();
        setSearchParams({}, { replace: true });
      });
    }
  }, [searchParams, api, refetch, setSearchParams]);

  const startOnboarding = async () => {
    setActionLoading(true);
    // Only send country on first-time setup; ignored once an account exists
    // (Stripe locks a connected account's country at creation).
    const payload = status?.connect_account_id ? undefined : { country };
    const res = await api.post<{ url: string }>('/connect/onboard', payload);
    setActionLoading(false);
    if (res.success && res.data?.url) {
      window.location.href = res.data.url;
    } else {
      toast.error(res.error || 'Could not start payout setup');
    }
  };

  const openDashboard = async () => {
    setActionLoading(true);
    const res = await api.post<{ url: string }>('/connect/login-link');
    setActionLoading(false);
    if (res.success && res.data?.url) {
      window.open(res.data.url, '_blank', 'noopener');
    } else {
      toast.error(res.error || 'Could not open payout dashboard');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-2xl font-bold">Payouts</h1>
          <p className="text-muted-foreground mt-1">
            Set up how you get paid your commission. We use Stripe to pay you securely.
          </p>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}

        {error && (
          <Card>
            <CardContent className="flex items-center gap-3 py-6 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <span>{(error as Error).message}</span>
            </CardContent>
          </Card>
        )}

        {status && (
          <>
            {/* Balance */}
            <div className="grid gap-4 sm:grid-cols-2">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Wallet className="h-4 w-4" /> Pending
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{formatGBP(status.pending_cents)}</div>
                  <p className="text-xs text-muted-foreground mt-1">Awaiting next payout</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <BadgeCheck className="h-4 w-4" /> Paid to date
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{formatGBP(status.paid_cents)}</div>
                  <p className="text-xs text-muted-foreground mt-1">Total commission paid</p>
                </CardContent>
              </Card>
            </div>

            {/* Onboarding status */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" /> Payout method
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {!status.connect_account_id && (
                  <>
                    <p className="text-muted-foreground">
                      You haven't set up payouts yet. Connect your bank account with Stripe so we
                      can pay your commission. It takes about 2 minutes.
                    </p>
                    <div className="max-w-xs space-y-1.5">
                      <label htmlFor="payout-country" className="text-sm font-medium">
                        Where do you bank?
                      </label>
                      <select
                        id="payout-country"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {PAYOUT_COUNTRIES.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-muted-foreground">
                        This sets the currency you're paid in and can't be changed later. Outside
                        the UK, you're paid in your local currency via Stripe.
                      </p>
                    </div>
                    <Button onClick={startOnboarding} disabled={actionLoading}>
                      {actionLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <CreditCard className="h-4 w-4 mr-2" />
                      )}
                      Set up payouts
                    </Button>
                  </>
                )}

                {status.connect_account_id && !status.payouts_enabled && (
                  <>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="border-yellow-500 text-yellow-500">
                        Setup incomplete
                      </Badge>
                    </div>
                    <p className="text-muted-foreground">
                      Stripe still needs a few more details before we can pay you. Pick up where you
                      left off.
                    </p>
                    <Button onClick={startOnboarding} disabled={actionLoading}>
                      {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Finish verification
                    </Button>
                  </>
                )}

                {status.connect_account_id && status.payouts_enabled && (
                  <>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-600 hover:bg-green-600">
                        <BadgeCheck className="h-3 w-3 mr-1" /> Payouts active
                      </Badge>
                    </div>
                    <p className="text-muted-foreground">
                      You're all set. Commission is paid to your connected bank account. View your
                      payout history and update details in your Stripe dashboard.
                    </p>
                    <Button variant="outline" onClick={openDashboard} disabled={actionLoading}>
                      {actionLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <ExternalLink className="h-4 w-4 mr-2" />
                      )}
                      View payout dashboard
                    </Button>
                  </>
                )}

                <p className="text-xs text-muted-foreground pt-2 border-t">
                  Your bank details are held securely by Stripe. ErgShoot never sees or stores them.
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
